import {
  createGlobalState,
  tryOnMounted,
  useBroadcastChannel,
  useDocumentVisibility,
  useEventListener,
  useIntervalFn,
  useTimestamp,
  useWindowFocus,
} from '@vueuse/core'
import {computed, ref, watch} from 'vue'
import {BroadcastChannelType} from '@toolify/client/src/modules/broadcast/enum/BroadcastChannelType'
import {BrowserTabsMessageType} from '@toolify/client/src/modules/broadcast/enum/BrowserTabsMessageType'
import {IBrowserTabState} from '@toolify/client/src/modules/broadcast/types/IBrowserTabState'
import {TBrowserTabsMessage} from '@toolify/client/src/modules/broadcast/types/TBrowserTabsMessage'
import {
  getPrimaryBrowserTabId,
  hasBrowserTabActiveClaim,
  isBrowserTabPresent,
} from '@toolify/client/src/modules/broadcast/functions/getPrimaryBrowserTabId'
import {isBrowserTabsMessage} from '@toolify/client/src/modules/broadcast/functions/isBrowserTabsMessage'

const TAB_STATE_HEARTBEAT_INTERVAL_MS = 10000
const TAB_STATE_FRESHNESS_CHECK_INTERVAL_MS = 5000

export const useBrowserTabs = createGlobalState(() => {
  const openedAt = Date.now()
  const tabId = `${openedAt}-${Math.random().toString(36).slice(2, 10)}`

  const documentVisibility = useDocumentVisibility()
  const isWindowFocused = useWindowFocus()
  const now = useTimestamp({interval: TAB_STATE_FRESHNESS_CHECK_INTERVAL_MS})

  const tabStates = ref<Map<string, IBrowserTabState>>(new Map<string, IBrowserTabState>())
  const lastActiveAt = ref(openedAt)

  const {
    data,
    post,
  } = useBroadcastChannel<TBrowserTabsMessage, TBrowserTabsMessage>({name: BroadcastChannelType.BrowserTabs})

  function isTabActive() {
    return isWindowFocused.value || documentVisibility.value === 'visible'
  }

  function syncLocalTabState(messageType: BrowserTabsMessageType.Hello | BrowserTabsMessageType.State) {
    const state: IBrowserTabState = {
      tabId,
      openedAt,
      lastActiveAt: lastActiveAt.value,
      updatedAt: Date.now(),
      isVisible: documentVisibility.value === 'visible',
      isFocused: isWindowFocused.value,
    }
    tabStates.value.set(tabId, state)
    post({
      type: messageType,
      state,
    })
  }

  function forgetAbsentTabs() {
    const currentTime = Date.now()
    for(const [knownTabId, tabState] of tabStates.value) {
      if(knownTabId === tabId || isBrowserTabPresent(tabState, currentTime)) {
        continue
      }
      tabStates.value.delete(knownTabId)
    }
  }

  watch([documentVisibility, isWindowFocused], () => {
    if(isTabActive()) {
      lastActiveAt.value = Date.now()
    }
    syncLocalTabState(BrowserTabsMessageType.State)
  }, {immediate: true})

  watch(data, (message) => {
    if(!isBrowserTabsMessage(message)) {
      return
    }
    if(message.type === BrowserTabsMessageType.Bye) {
      tabStates.value.delete(message.tabId)
      return
    }
    if(message.state.tabId === tabId) {
      return
    }
    tabStates.value.set(message.state.tabId, message.state)
    if(message.type === BrowserTabsMessageType.Hello) {
      syncLocalTabState(BrowserTabsMessageType.State)
    }
  })

  useIntervalFn(() => {
    forgetAbsentTabs()
    const currentTime = Date.now()
    const electedTabId = getPrimaryBrowserTabId([...tabStates.value.values()], tabId, currentTime)
    const electedTabState = electedTabId === tabId ? null : tabStates.value.get(electedTabId)
    const shouldAskEveryoneToReannounce = !!electedTabState && !hasBrowserTabActiveClaim(electedTabState, currentTime)
    syncLocalTabState(shouldAskEveryoneToReannounce
      ? BrowserTabsMessageType.Hello
      : BrowserTabsMessageType.State)
  }, TAB_STATE_HEARTBEAT_INTERVAL_MS)

  useEventListener(window, ['beforeunload', 'pagehide'], () => {
    tabStates.value.delete(tabId)
    post({
      type: BrowserTabsMessageType.Bye,
      tabId,
    })
  })

  useEventListener(window, 'pageshow', () => {
    syncLocalTabState(BrowserTabsMessageType.Hello)
  })

  tryOnMounted(() => {
    syncLocalTabState(BrowserTabsMessageType.Hello)
  })

  const primaryTabId = computed(() => {
    return getPrimaryBrowserTabId([...tabStates.value.values()], tabId, now.value)
  })

  const isPrimaryTab = computed(() => {
    return primaryTabId.value === tabId
  })

  const activeBrowserTabIds = computed(() => {
    return new Set<string>(tabStates.value.keys())
  })

  return {
    activeBrowserTabIds,
    isPrimaryTab,
    primaryTabId,
  }
})
